import React, { useMemo } from 'react';

export const DEFAULT_THEME = {
  preset: 'beige',
  background: '#f8f3ea',
  surface: '#fffaf2',
  text: '#3b2c24',
  primary: '#7b4f2f',
  secondary: '#a67c5b',
  cardBorder: '#e8dccb',
  radius: 14,
};

export function normalizeTheme(input) {
  const t = input && typeof input === 'object' ? input : {};
  const radiusNum = Number(t.radius);
  return {
    preset: String(t.preset || DEFAULT_THEME.preset),
    background: String(t.background || DEFAULT_THEME.background),
    surface: String(t.surface || DEFAULT_THEME.surface),
    text: String(t.text || DEFAULT_THEME.text),
    primary: String(t.primary || DEFAULT_THEME.primary),
    secondary: String(t.secondary || DEFAULT_THEME.secondary),
    cardBorder: String(t.cardBorder || DEFAULT_THEME.cardBorder),
    radius: Number.isFinite(radiusNum) ? Math.max(6, Math.min(28, Math.round(radiusNum))) : DEFAULT_THEME.radius,
  };
}

export function splitThemeAndBlocks(blocks) {
  const list = Array.isArray(blocks) ? blocks : [];
  const themeBlock = list.find((b) => b && b.type === 'theme');
  return {
    theme: normalizeTheme(themeBlock || {}),
    blocks: list.filter((b) => b && b.type !== 'theme'),
  };
}

function buttonStyle(theme, variant) {
  if (variant === 'secondary') {
    return {
      background: theme.secondary,
      color: '#fff',
      border: `1px solid ${theme.secondary}`,
    };
  }
  if (variant === 'outline') {
    return {
      background: 'transparent',
      color: theme.primary,
      border: `1px solid ${theme.primary}`,
    };
  }
  return {
    background: theme.primary,
    color: '#fff',
    border: `1px solid ${theme.primary}`,
  };
}

function renderBlock(block, theme) {
  if (!block || typeof block !== 'object') return null;
  if (block.type === 'heading') {
    const lv = Number(block.level) || 2;
    const st = { margin: 0, color: theme.text, lineHeight: 1.4 };
    if (lv === 1) return <h1 style={{ ...st, fontSize: 34 }}>{block.text || ''}</h1>;
    if (lv === 3) return <h3 style={{ ...st, fontSize: 22 }}>{block.text || ''}</h3>;
    return <h2 style={{ ...st, fontSize: 28 }}>{block.text || ''}</h2>;
  }
  if (block.type === 'text') {
    return (
      <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.85, margin: 0, color: theme.text }}>
        {block.text || ''}
      </p>
    );
  }
  if (block.type === 'image') {
    if (!block.url) return <div style={{ color: '#8c7b6a' }}>尚未設定圖片</div>;
    const radius = Number.isFinite(Number(block.radius)) ? Number(block.radius) : theme.radius;
    return (
      <img
        src={block.url}
        alt={block.alt || ''}
        style={{
          maxWidth: '100%',
          width: '100%',
          borderRadius: radius,
          display: 'block',
          boxShadow: '0 8px 24px rgba(80, 45, 20, 0.12)',
        }}
      />
    );
  }
  if (block.type === 'button') {
    const style = buttonStyle(theme, String(block.variant || 'primary'));
    if (!block.url && !block.label) return <div style={{ color: '#8c7b6a' }}>尚未設定按鈕</div>;
    return (
      <a
        href={block.url || '#'}
        target={block.newTab === false ? undefined : '_blank'}
        rel="noreferrer"
        style={{
          display: 'inline-block',
          padding: '11px 18px',
          borderRadius: 10,
          textDecoration: 'none',
          fontWeight: 700,
          boxShadow: '0 8px 18px rgba(80, 45, 20, 0.14)',
          ...style,
        }}
      >
        {block.label || '前往連結'}
      </a>
    );
  }
  return null;
}

export default function CMSRenderer({ blocks, pageTitle, pageDesc }) {
  const data = useMemo(() => splitThemeAndBlocks(blocks), [blocks]);
  const theme = data.theme;
  const list = data.blocks;

  return (
    <main
      style={{
        minHeight: '100%',
        background: `linear-gradient(180deg, ${theme.background} 0%, #fff 45%)`,
        padding: '26px 12px 40px',
      }}
    >
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <header style={{ marginBottom: 18 }}>
          <h1 style={{ marginTop: 0, marginBottom: 8, color: theme.text }}>{pageTitle || '未命名頁面'}</h1>
          {pageDesc ? <p style={{ margin: 0, color: theme.text, opacity: 0.82, lineHeight: 1.7 }}>{pageDesc}</p> : null}
        </header>
        <div style={{ display: 'grid', gap: 18 }}>
          {list.map((b, idx) => (
            <section
              key={b.id || idx}
              style={{
                background: theme.surface,
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: theme.radius,
                padding: 14,
              }}
            >
              {renderBlock(b, theme)}
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
